import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { LoadingState } from '@/components/common/States';
import { SelectField, TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { incidentsApi, tripsApi, transportRoutesApi, vehiclesApi } from '@/services/api/endpoints/transport';
import { studentsApi } from '@/services/api/endpoints/students';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type {
  IncidentInput,
  IncidentSeverity,
  IncidentStatus,
  IncidentType,
  TransportIncident,
} from '@/types/transport';
import { formatDateTime } from '@/utils/format';
import { Badge, Field, IncidentStatusBadge, SeverityBadge, humanise } from './shared';

const TYPES: IncidentType[] = [
  'BREAKDOWN',
  'ACCIDENT',
  'STUDENT_INCIDENT',
  'DRIVER_ISSUE',
  'ROUTE_DELAY',
  'MISSING_STUDENT',
  'MEDICAL_EMERGENCY',
  'OTHER',
];

const SEVERITIES: IncidentSeverity[] = ['MINOR', 'MODERATE', 'SERIOUS', 'CRITICAL'];

const STATUSES: { value: IncidentStatus; label: string }[] = [
  { value: 'REPORTED', label: 'Reported' },
  { value: 'UNDER_REVIEW', label: 'Under review' },
  { value: 'ESCALATED', label: 'Escalated' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

/** A follow-up that carries text with it. Review and close carry none. */
type TextAction = 'escalate' | 'action' | 'resolve';

/**
 * Incidents and the escalation chain (§26/§27).
 *
 * <p>The list leads with severity, not date: yesterday's CRITICAL outranks
 * this morning's MINOR. The narrative can name children and staff, which is
 * why the whole screen sits behind the incident permission rather than the
 * timetable one.
 *
 * <p>Resolution requires text. An incident closed with no record of how is an
 * incident the next parent phone call reopens. CRITICAL severity and a missing
 * student escalate themselves on the server — the report form does not depend
 * on anyone finding an escalate button mid-crisis.
 */
export function IncidentsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('severity,desc');

  const manages = can(PERMISSIONS.TRANSPORT_INCIDENT_MANAGE);

  const [severity, setSeverity] = useState<IncidentSeverity | ''>('');
  const [status, setStatus] = useState<IncidentStatus | ''>('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const [acting, setActing] = useState<TextAction | null>(null);
  const [actionText, setActionText] = useState('');

  // ------------------------------------------------------------ report form

  const [incidentType, setIncidentType] = useState<IncidentType | ''>('');
  const [formSeverity, setFormSeverity] = useState<IncidentSeverity | ''>('');
  const [occurredAt, setOccurredAt] = useState('');
  const [tripId, setTripId] = useState('');
  const [routeId, setRouteId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [peopleInvolved, setPeopleInvolved] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [involved, setInvolved] = useState<{ id: string; name: string }[]>([]);

  const resetForm = () => {
    setIncidentType('');
    setFormSeverity('');
    setOccurredAt('');
    setTripId('');
    setRouteId('');
    setVehicleId('');
    setLocation('');
    setDescription('');
    setPeopleInvolved('');
    setActionTaken('');
    setStudentSearch('');
    setInvolved([]);
  };

  const params = {
    ...table.params,
    ...(severity === '' ? {} : { severity }),
    ...(status === '' ? {} : { status }),
  };

  const incidents = useQuery({
    queryKey: queryKeys.transportIncidents.list(params),
    queryFn: () => incidentsApi.list(params),
  });

  const detail = useQuery({
    queryKey: queryKeys.transportIncidents.detail(viewingId ?? ''),
    queryFn: () => incidentsApi.get(viewingId ?? ''),
    enabled: viewingId !== null,
  });

  /* Reference data for the report form, loaded only while it is open and only
     for holders of the matching view permission — an unpermitted select is
     simply absent rather than a failed request. */
  const todaysTrips = useQuery({
    queryKey: queryKeys.trips.board(undefined),
    queryFn: () => tripsApi.board(),
    enabled: reporting && can(PERMISSIONS.TRANSPORT_TRIP_VIEW),
  });

  const routes = useQuery({
    queryKey: queryKeys.transportRoutes.list({ page: 0, size: 200 }),
    queryFn: () => transportRoutesApi.list({ page: 0, size: 200 }),
    enabled: reporting && can(PERMISSIONS.TRANSPORT_ROUTE_VIEW),
  });

  const vehicles = useQuery({
    queryKey: queryKeys.vehicles.list({ page: 0, size: 200 }),
    queryFn: () => vehiclesApi.list({ page: 0, size: 200 }),
    enabled: reporting && can(PERMISSIONS.VEHICLE_VIEW),
  });

  const studentMatches = useQuery({
    queryKey: queryKeys.students.list({ page: 0, size: 8, search: studentSearch.trim() }),
    queryFn: () => studentsApi.list({ page: 0, size: 8, search: studentSearch.trim() }),
    enabled: reporting && can(PERMISSIONS.STUDENT_VIEW) && studentSearch.trim().length >= 2,
  });

  const report = useMutation({
    mutationFn: () => {
      const payload: IncidentInput = {
        incidentType: incidentType as IncidentType,
        severity: formSeverity as IncidentSeverity,
        description: description.trim(),
        ...(occurredAt === '' ? {} : { occurredAt: new Date(occurredAt).toISOString() }),
        // The trip carries its route and vehicle; naming them as well would
        // invite the two to disagree.
        ...(tripId === '' ? {} : { tripId }),
        ...(tripId === '' && routeId !== '' ? { routeId } : {}),
        ...(tripId === '' && vehicleId !== '' ? { vehicleId } : {}),
        ...(location.trim() === '' ? {} : { location: location.trim() }),
        ...(peopleInvolved.trim() === '' ? {} : { peopleInvolved: peopleInvolved.trim() }),
        ...(actionTaken.trim() === '' ? {} : { actionTaken: actionTaken.trim() }),
        ...(involved.length === 0 ? {} : { studentIds: involved.map((student) => student.id) }),
      };
      return incidentsApi.report(payload);
    },
    onSuccess: (incident) => {
      toast.success(`${incident.incidentNumber} reported — ${humanise(incident.status)}`);
      setReporting(false);
      resetForm();
      void queryClient.invalidateQueries({ queryKey: queryKeys.transportIncidents.all });
    },
    onError: (error) => handleApiError(error),
  });

  const transition = useMutation({
    mutationFn: (input: {
      id: string;
      kind: 'review' | 'escalate' | 'action' | 'resolve' | 'close';
      text?: string;
    }) => {
      if (input.kind === 'review') {
        return incidentsApi.review(input.id);
      }
      if (input.kind === 'escalate') {
        return incidentsApi.escalate(input.id, input.text);
      }
      if (input.kind === 'action') {
        return incidentsApi.recordAction(input.id, input.text ?? '');
      }
      if (input.kind === 'resolve') {
        return incidentsApi.resolve(input.id, input.text ?? '');
      }
      return incidentsApi.close(input.id);
    },
    onSuccess: (incident, input) => {
      toast.success(
        input.kind === 'action'
          ? `Action recorded on ${incident.incidentNumber}`
          : `${incident.incidentNumber} is now ${humanise(incident.status)}`,
      );
      queryClient.setQueryData(queryKeys.transportIncidents.detail(incident.id), incident);
      void queryClient.invalidateQueries({ queryKey: queryKeys.transportIncidents.all });
      setActing(null);
      setActionText('');
    },
    onError: (error) => handleApiError(error),
  });

  const columns: Column<TransportIncident>[] = [
    {
      key: 'incidentNumber',
      header: 'Incident',
      render: (row) => (
        <span>
          <span className="block font-mono text-[12px] text-ink">{row.incidentNumber}</span>
          <span className="block text-[11px] text-subtle">{formatDateTime(row.occurredAt)}</span>
        </span>
      ),
    },
    {
      key: 'what',
      header: 'What',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{humanise(row.incidentType)}</span>
          {row.location !== undefined && (
            <span className="block text-[11px] text-subtle">{row.location}</span>
          )}
        </span>
      ),
    },
    {
      key: 'severity',
      header: 'Severity',
      sortKey: 'severity',
      render: (row) => <SeverityBadge severity={row.severity} />,
    },
    {
      key: 'route',
      header: 'Route',
      render: (row) => <span className="text-sm">{row.routeName ?? '—'}</span>,
    },
    {
      key: 'students',
      header: 'Students',
      className: 'text-right',
      render: (row) => <span className="tabular-nums">{row.students.length}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <IncidentStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => (
        <Button size="sm" variant="secondary" onClick={() => setViewingId(row.id)}>
          View
        </Button>
      ),
    },
  ];

  const incident = detail.data;

  /** The follow-up buttons the current status allows. Everything is server-checked again. */
  const followUps =
    incident === undefined || !manages ? null : (
      <>
        {incident.status === 'REPORTED' && (
          <Button
            variant="secondary"
            loading={transition.isPending && transition.variables?.kind === 'review'}
            onClick={() => transition.mutate({ id: incident.id, kind: 'review' })}
          >
            Take under review
          </Button>
        )}
        {(incident.status === 'REPORTED' || incident.status === 'UNDER_REVIEW') && (
          <Button variant="secondary" onClick={() => setActing('escalate')}>
            Escalate…
          </Button>
        )}
        {(incident.status === 'REPORTED' ||
          incident.status === 'UNDER_REVIEW' ||
          incident.status === 'ESCALATED') && (
          <>
            <Button variant="ghost" onClick={() => setActing('action')}>
              Record action…
            </Button>
            <Button onClick={() => setActing('resolve')}>Resolve…</Button>
          </>
        )}
        {incident.status === 'RESOLVED' && (
          <Button
            loading={transition.isPending && transition.variables?.kind === 'close'}
            onClick={() => transition.mutate({ id: incident.id, kind: 'close' })}
          >
            Close
          </Button>
        )}
      </>
    );

  return (
    <>
      <ListPage
        title="Incidents"
        description="What went wrong on the road, worst first, and what was done about it."
        icon={<AlertTriangle className="h-5 w-5" aria-hidden="true" />}
        action={
          manages ? <Button onClick={() => setReporting(true)}>Report an incident</Button> : undefined
        }
        filters={
          <>
            <FilterSelect
              label="Severity"
              value={severity}
              onChange={(value) => {
                setSeverity(value);
                table.setPage(0);
              }}
              options={SEVERITIES.map((value) => ({ value, label: humanise(value) }))}
            />
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={STATUSES}
            />
          </>
        }
        pagination={incidents.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <DataTable
          columns={columns}
          rows={incidents.data?.data}
          rowKey={(row) => row.id}
          loading={incidents.isPending}
          error={incidents.error}
          onRetry={() => void incidents.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          onRowClick={(row) => setViewingId(row.id)}
          emptyTitle="No incidents"
          emptyDescription="Nothing has been reported."
        />
      </ListPage>

      <Modal
        open={reporting}
        onClose={() => setReporting(false)}
        title="Report an incident"
        description="Critical severity and a missing student escalate themselves and notify the involved students' guardians."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReporting(false)}>
              Cancel
            </Button>
            <Button
              loading={report.isPending}
              disabled={incidentType === '' || formSeverity === '' || description.trim() === ''}
              onClick={() => report.mutate()}
            >
              Report it
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <SelectField
              label="What happened"
              required
              value={incidentType}
              onChange={(event) => setIncidentType(event.target.value as IncidentType | '')}
            >
              <option value="">Select…</option>
              {TYPES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
            <SelectField
              label="Severity"
              required
              value={formSeverity}
              onChange={(event) => setFormSeverity(event.target.value as IncidentSeverity | '')}
            >
              <option value="">Select…</option>
              {SEVERITIES.map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </SelectField>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <TextField
              label="When"
              type="datetime-local"
              value={occurredAt}
              onChange={(event) => setOccurredAt(event.target.value)}
              hint="Leave blank for now."
            />
            <TextField
              label="Where"
              value={location}
              onChange={(event) => setLocation(event.target.value)}
            />
          </div>

          {can(PERMISSIONS.TRANSPORT_TRIP_VIEW) && (
            <SelectField
              label="Trip (today)"
              value={tripId}
              onChange={(event) => setTripId(event.target.value)}
              hint="Naming the trip fills in the route and vehicle."
            >
              <option value="">Not on a trip / another day</option>
              {todaysTrips.data?.map((trip) => (
                <option key={trip.id} value={trip.id}>
                  {trip.routeName} · {trip.tripNumber} ({humanise(trip.direction)})
                </option>
              ))}
            </SelectField>
          )}

          {tripId === '' && (
            <div className="grid gap-3 sm:grid-cols-2">
              {can(PERMISSIONS.TRANSPORT_ROUTE_VIEW) && (
                <SelectField
                  label="Route"
                  value={routeId}
                  onChange={(event) => setRouteId(event.target.value)}
                >
                  <option value="">None</option>
                  {routes.data?.data.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.displayName}
                    </option>
                  ))}
                </SelectField>
              )}
              {can(PERMISSIONS.VEHICLE_VIEW) && (
                <SelectField
                  label="Vehicle"
                  value={vehicleId}
                  onChange={(event) => setVehicleId(event.target.value)}
                >
                  <option value="">None</option>
                  {vehicles.data?.data.map((vehicle) => (
                    <option key={vehicle.id} value={vehicle.id}>
                      {vehicle.displayName}
                    </option>
                  ))}
                </SelectField>
              )}
            </div>
          )}

          <TextField
            label="What happened, in your words"
            required
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />

          <TextField
            label="People involved"
            value={peopleInvolved}
            onChange={(event) => setPeopleInvolved(event.target.value)}
            hint="Everyone who is not a student — drivers, other motorists, bystanders."
          />

          {can(PERMISSIONS.STUDENT_VIEW) && (
            <div>
              <TextField
                label="Students involved"
                value={studentSearch}
                onChange={(event) => setStudentSearch(event.target.value)}
                placeholder="Search by name or number…"
                hint="Their guardians are notified on escalation — kind and severity only."
              />
              {involved.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {involved.map((student) => (
                    <span
                      key={student.id}
                      className="inline-flex items-center gap-1 rounded-md border border-line bg-surface-2 px-2 py-1 text-xs text-ink"
                    >
                      {student.name}
                      <button
                        type="button"
                        aria-label={`Remove ${student.name}`}
                        onClick={() =>
                          setInvolved((current) =>
                            current.filter((entry) => entry.id !== student.id),
                          )
                        }
                      >
                        <X className="h-3 w-3" aria-hidden="true" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {studentSearch.trim().length >= 2 && (
                <ul className="mt-2 divide-y divide-line rounded-lg border border-line">
                  {(studentMatches.data?.data ?? [])
                    .filter((student) => !involved.some((entry) => entry.id === student.id))
                    .map((student) => (
                      <li key={student.id}>
                        <button
                          type="button"
                          className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm text-ink hover:bg-surface-2"
                          onClick={() => {
                            setInvolved((current) => [
                              ...current,
                              { id: student.id, name: student.fullName },
                            ]);
                            setStudentSearch('');
                          }}
                        >
                          <span className="min-w-0 truncate">{student.fullName}</span>
                          <span className="shrink-0 font-mono text-[11px] text-subtle">
                            {student.studentNumber}
                          </span>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}

          <TextField
            label="Action already taken"
            value={actionTaken}
            onChange={(event) => setActionTaken(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={viewingId !== null}
        onClose={() => setViewingId(null)}
        title={incident?.incidentNumber ?? 'Incident'}
        description="The incident and its trail."
        size="lg"
        footer={followUps === null ? undefined : <>{followUps}</>}
      >
        {detail.isPending ? (
          <LoadingState label="Loading the incident" />
        ) : incident === undefined ? null : (
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <SeverityBadge severity={incident.severity} />
              <IncidentStatusBadge status={incident.status} />
              {incident.guardianNotified && <Badge tone="info">guardians notified</Badge>}
            </div>
            <dl>
              <Field label="What">{humanise(incident.incidentType)}</Field>
              <Field label="When">{formatDateTime(incident.occurredAt)}</Field>
              {incident.location !== undefined && (
                <Field label="Where">{incident.location}</Field>
              )}
              {incident.routeName !== undefined && (
                <Field label="Route">{incident.routeName}</Field>
              )}
              {incident.reportedByName !== undefined && (
                <Field label="Reported by">{incident.reportedByName}</Field>
              )}
              {incident.peopleInvolved !== undefined && (
                <Field label="People involved">{incident.peopleInvolved}</Field>
              )}
              {incident.students.length > 0 && (
                <Field label="Students">
                  {incident.students.map((student) => student.studentName).join(', ')}
                </Field>
              )}
            </dl>

            <p className="mt-3 whitespace-pre-wrap text-sm text-ink">{incident.description}</p>

            <dl className="mt-3">
              {incident.actionTaken !== undefined && (
                <Field label="Action taken">{incident.actionTaken}</Field>
              )}
              {incident.escalatedAt !== undefined && (
                <Field label="Escalated">
                  {formatDateTime(incident.escalatedAt)}
                  {incident.escalationNotes !== undefined ? ` — ${incident.escalationNotes}` : ''}
                </Field>
              )}
              {incident.resolvedAt !== undefined && (
                <Field label="Resolved">
                  {formatDateTime(incident.resolvedAt)}
                  {incident.resolution !== undefined ? ` — ${incident.resolution}` : ''}
                </Field>
              )}
            </dl>
          </div>
        )}
      </Modal>

      <Modal
        open={acting !== null && incident !== undefined}
        onClose={() => {
          setActing(null);
          setActionText('');
        }}
        title={
          acting === 'escalate'
            ? 'Escalate to administration'
            : acting === 'action'
              ? 'Record the action taken'
              : 'Resolve the incident'
        }
        description={
          acting === 'escalate'
            ? "Notifies the involved students' guardians — kind and severity only, never the narrative."
            : acting === 'action'
              ? 'What was actually done, for the record.'
              : 'Required. A resolution nobody wrote down is a resolution nobody can point to later.'
        }
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setActing(null);
                setActionText('');
              }}
            >
              Cancel
            </Button>
            <Button
              loading={transition.isPending}
              disabled={acting !== 'escalate' && actionText.trim() === ''}
              onClick={() => {
                if (incident !== undefined && acting !== null) {
                  transition.mutate({
                    id: incident.id,
                    kind: acting,
                    text: actionText.trim() === '' ? undefined : actionText.trim(),
                  });
                }
              }}
            >
              {acting === 'escalate' ? 'Escalate' : acting === 'action' ? 'Record it' : 'Resolve it'}
            </Button>
          </>
        }
      >
        <TextField
          label={acting === 'escalate' ? 'Notes' : acting === 'action' ? 'Action taken' : 'Resolution'}
          required={acting !== 'escalate'}
          value={actionText}
          onChange={(event) => setActionText(event.target.value)}
        />
      </Modal>
    </>
  );
}
